// ─────────────────────────────────────────────────────────────────────────────
// Estado del formulario de pop-ups y su traducción al contrato del backend
// (`contracts/announcements`). Funciones puras: sin React ni i18n — los
// mensajes de error son claves en español que la vista pasa por t().
// ─────────────────────────────────────────────────────────────────────────────

export const LIMITS = {
  title: 120,
  body: 5000,
  imageAlt: 200,
  ctaLabel: 40,
  url: 2048,
};

const LINK_PREFIXES = ["https://", "mailto:", "tel:"];

function pad(n) {
  return String(n).padStart(2, "0");
}

/** ISO → valor de `<input type="datetime-local">` en la hora local del navegador. */
export function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Valor de `datetime-local` (hora local) → ISO UTC, o null. */
export function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** ¿Es un link que el backend acepta (https, mailto o tel)? */
export function isAllowedLink(url) {
  const value = String(url ?? "").trim();
  return LINK_PREFIXES.some((prefix) => value.toLowerCase().startsWith(prefix));
}

/**
 * Formulario vacío.
 * @param {{ systemMode: boolean, now?: Date }} opts
 */
export function emptyForm({ systemMode, now = new Date() } = {}) {
  return {
    scope: systemMode ? "platform" : "operator",
    logisticsCompanyId: "",
    title: "",
    body: "",
    imageUrl: "",
    imageAlt: "",
    ctaEnabled: false,
    ctaLabel: "",
    ctaUrl: "",
    startsAt: isoToLocalInput(now.toISOString()),
    endsAt: "",
    audience: {
      mode: "all",
      match: "all",
      integrations: [],
      communes: [],
      operatorIds: [],
    },
  };
}

/** Pop-up del backend → formulario. */
export function formFromAnnouncement(announcement) {
  const audience = announcement?.audience ?? {};
  return {
    scope: announcement?.scope ?? "operator",
    logisticsCompanyId: announcement?.operator?.id ?? "",
    title: announcement?.title ?? "",
    body: announcement?.body ?? "",
    imageUrl: announcement?.imageUrl ?? "",
    imageAlt: announcement?.imageAlt ?? "",
    ctaEnabled: Boolean(announcement?.cta),
    ctaLabel: announcement?.cta?.label ?? "",
    ctaUrl: announcement?.cta?.url ?? "",
    startsAt: isoToLocalInput(announcement?.startsAt),
    endsAt: isoToLocalInput(announcement?.endsAt),
    audience: {
      mode: audience.mode === "filtered" ? "filtered" : "all",
      match: audience.match === "any" ? "any" : "all",
      integrations: (audience.integrations ?? []).map((i) => i.label),
      communes: (audience.communes ?? []).map((c) => ({
        communeId: c.communeId,
        name: c.name,
      })),
      operatorIds: audience.operatorIds ?? [],
    },
  };
}

/** Audiencia del formulario → `audience` del backend. */
export function audiencePayload(form) {
  const filtered = form.audience.mode === "filtered";
  return {
    mode: form.audience.mode,
    match: form.audience.match,
    integrations: filtered ? form.audience.integrations : [],
    communeIds: filtered ? form.audience.communes.map((c) => c.communeId) : [],
    operatorIds: form.scope === "platform" ? form.audience.operatorIds : [],
  };
}

/** Dueño para la vista previa y las opciones (solo el superadmin del sistema lo manda). */
export function ownerParams(form, { systemMode }) {
  if (!systemMode) return {};
  return form.scope === "operator"
    ? { scope: "operator", logisticsCompanyId: form.logisticsCompanyId || undefined }
    : { scope: "platform" };
}

/**
 * Formulario → cuerpo de POST (nuevo) o PATCH (edición).
 *
 * En una edición, `null` QUITA la imagen, el botón o la fecha de término; en
 * uno nuevo esos campos simplemente no se mandan. El dueño solo viaja al
 * crear (en el backend es inmutable).
 */
export function toPayload(form, { systemMode, isNew }) {
  const absent = isNew ? undefined : null;
  const imageUrl = form.imageUrl.trim();
  const payload = {
    title: form.title.trim(),
    body: form.body,
    imageUrl: imageUrl || absent,
    imageAlt: imageUrl ? form.imageAlt.trim() || absent : absent,
    cta: form.ctaEnabled
      ? { label: form.ctaLabel.trim(), url: form.ctaUrl.trim() }
      : absent,
    audience: audiencePayload(form),
    startsAt: localInputToIso(form.startsAt),
    endsAt: form.endsAt ? localInputToIso(form.endsAt) : absent,
  };
  if (isNew && systemMode) {
    payload.scope = form.scope;
    if (form.scope === "operator") payload.logisticsCompanyId = form.logisticsCompanyId;
  }
  return payload;
}

/**
 * Validación de cliente (el backend vuelve a validar todo). Devuelve un mapa
 * campo → clave de mensaje; vacío = válido.
 */
export function validateForm(form, { systemMode, isNew }) {
  const errors = {};
  const title = form.title.trim();
  if (!title) errors.title = "El título es obligatorio";
  else if (title.length > LIMITS.title) errors.title = "El título es demasiado largo";

  if (form.body.length > LIMITS.body) errors.body = "El texto es demasiado largo";

  if (form.ctaEnabled) {
    if (!form.ctaLabel.trim()) errors.ctaLabel = "Escribe el texto del botón";
    else if (form.ctaLabel.trim().length > LIMITS.ctaLabel) {
      errors.ctaLabel = "El texto del botón es demasiado largo";
    }
    if (!isAllowedLink(form.ctaUrl)) {
      errors.ctaUrl = "El link debe empezar con https://, mailto: o tel:";
    }
  }

  const startsAt = localInputToIso(form.startsAt);
  if (!startsAt) errors.startsAt = "Indica desde cuándo se muestra";
  if (form.endsAt) {
    const endsAt = localInputToIso(form.endsAt);
    if (!endsAt) errors.endsAt = "Fecha de término no válida";
    else if (startsAt && endsAt <= startsAt) {
      errors.endsAt = "La fecha de término debe ser posterior a la de inicio";
    }
  }

  if (
    form.audience.mode === "filtered" &&
    form.audience.integrations.length === 0 &&
    form.audience.communes.length === 0
  ) {
    errors.audience = "Elige al menos una integración o una comuna";
  }

  if (systemMode && isNew && form.scope === "operator" && !form.logisticsCompanyId) {
    errors.logisticsCompanyId = "Elige el operador";
  }
  return errors;
}

/**
 * Estado visible de un pop-up: además del `status` guardado, distingue los
 * publicados que todavía no empiezan o que ya vencieron.
 * @returns {"draft"|"scheduled"|"live"|"expired"|"archived"}
 */
export function displayStatus(announcement, now = new Date()) {
  if (announcement.status === "archived") return "archived";
  if (announcement.status === "draft") return "draft";
  if (announcement.endsAt && new Date(announcement.endsAt) <= now) return "expired";
  if (new Date(announcement.startsAt) > now) return "scheduled";
  return "live";
}
